import { EpgEvent, StreamInfo, StreamInfoType } from './StreamInfo';
import { StreamType } from '../../../api/models/Shared.types';
import {
  calcEpgStartPosition,
  convertLivePlayerTimes,
  epgAnalyticsProperties,
  getEpgMetaInfo,
} from './StreamInfoUtility';
import { Bookmark } from '../logicHandlers/Bookmark';
import { IBookmarkElementV1, IProperty, IScheduleSummary } from '../../../api/models/AndromeTV.types';
import { Backend } from '../../../api/Backend';
import { timeStringToMs } from '../../utility/DateFormatter';

export class ReplayStreamInfo extends StreamInfo {
  private _epgEvent: EpgEvent;
  private _startPositionType: number;
  private _metaInfo: string;
  private _resumeFromLive = false;
  /**
   * @param {EpgEvent} epgEvent
   * @param {number} startPosition
   * @param {Backend} backend
   * @param {IBookmarkElementV1} bookmark?
   */
  constructor(epgEvent: EpgEvent, startPosition: number, backend: Backend, bookmark?: IBookmarkElementV1) {
    super();
    this._epgEvent = epgEvent;
    this.bookmark = Bookmark.fromProgram(epgEvent.program, bookmark ? bookmark.position : 0, epgEvent.series, backend);
    this._startPositionType = startPosition;

    this._metaInfo = getEpgMetaInfo(backend, epgEvent.schedule.published);
  }

  override get isAdult() {
    return this._epgEvent.program?.adult;
  }

  override get assetId() {
    return this._epgEvent.schedule.id;
  }

  override get resourceId() {
    return this._epgEvent.schedule.id;
  }

  override get type() {
    return StreamInfoType.REPLAY;
  }

  override get titleText() {
    return this._epgEvent.program.title || '';
  }

  override get metaInfo() {
    return this._metaInfo;
  }

  override get channelName() {
    return this._epgEvent.channel.text.title || '';
  }

  override get streamType() {
    return StreamType.REPLAY;
  }

  override get startPositionTime() {
    if (this.bookmark && this.currentAsset) {
      return calcEpgStartPosition(
        this._startPositionType,
        this.currentAsset.event,
        this.bookmark.value.position,
        this.currentAsset.bookmarkOffset ?? 0,
        this.bookmark.endOffset
      );
    }
  }

  override get startOffset() {
    return (this.currentAsset ? this.currentAsset.bookmarkOffset || 0 : 0) / 1000;
  }

  override set resumeFromLive(resume) {
    this._resumeFromLive = resume;
  }

  override get resumeFromLive() {
    return this._resumeFromLive;
  }

  override convertPlayTimes(pos: number, duration: number, isLive: boolean) {
    if (isLive && this.currentAsset?.event) {
      return convertLivePlayerTimes(pos, this.currentAsset?.event);
    } else {
      return super.convertPlayTimes(pos, duration, isLive);
    }
  }

  override get episode(): number {
    if (this._epgEvent.program) {
      return this._epgEvent.program.episode ?? 0;
    } else {
      return 0;
    }
  }

  override get season(): number {
    if (this._epgEvent.season) {
      return this._epgEvent.season.season ?? 0;
    } else {
      return 0;
    }
  }

  override updateBookmark(position: number, duration: number) {
    if (this._epgEvent.schedule) {
      duration =
        timeStringToMs(this._epgEvent.schedule.published.end) - timeStringToMs(this._epgEvent.schedule.published.start);
      // divide by 1000 to have both position and duration in seconds
      duration = duration / 1000;
    }
    this.bookmark?.updateTime(position - this.startOffset, duration);
    return true;
  }

  override get schedule(): IScheduleSummary {
    return this._epgEvent.schedule;
  }

  override get channel() {
    return this._epgEvent.channel;
  }

  override generateAnalyticsProperties(excluded: string[]): IProperty[] {
    const properties = super.generateAnalyticsProperties(excluded);
    properties.push(...epgAnalyticsProperties(this._epgEvent, excluded));
    return properties;
  }

  override updateStartPositionType(type: number) {
    this._startPositionType = type;
  }
}
